import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Shield,
  Check,
  CheckSquare,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import TabPageLayout, { PageLoader } from './TabPageLayout';
import EmptyState from './EmptyState';
import { useRoles } from '../hooks/useData';
import { PrimaryButton, SecondaryButton, DangerButton, SuccessButton } from './Buttons';
import StandardModal from './StandardModal';
import InfoCard from './InfoCard';
import MassActionToolbar from './MassActionToolbar';
import CardListViewToggle from './CardListViewToggle';
import ResourceCard from './ResourceCard';
import { getAllTabIds } from '../config/navigation';

const PRESET_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#14B8A6', // Teal
  '#F97316', // Orange
];

const RolesTab = () => {
  const { roles, isLoading, isError, refresh } = useRoles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [massActionMode, setMassActionMode] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [viewMode, setViewMode] = useState("card");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    permissions: [],
    tabPermissions: {}
  });
  
  const [modalStep, setModalStep] = useState(1);

  const handleOpenDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        name: role.name,
        description: role.description || '',
        color: role.color || '#3B82F6',
        permissions: role.permissions || ['view_bookings'],
        tabPermissions: role.tabPermissions || {}
      });
    } else {
      setEditingRole(null);
      setFormData({
        name: '',
        description: '',
        color: '#3B82F6',
        permissions: ['view_bookings'],
        tabPermissions: {}
      });
    }
    setDialogOpen(true);
    setModalStep(1);
  };

  const handleSaveRole = async () => {
    if (!formData.name.trim()) {
      toast.error('Role name is required');
      return;
    }

    setIsSaving(true);
    try {
      if (editingRole) {
        await api.put(`/roles/${editingRole.id}`, formData);
        toast.success(`Role "${formData.name}" updated successfully!`);
      } else {
        await api.post('/roles', formData);
        toast.success(`Role "${formData.name}" created successfully!`);
      }
      
      window.dispatchEvent(new Event('setupDataChanged'));
      setDialogOpen(false);
      refresh();
    } catch (error) {
      console.error('Error saving role:', error);
      toast.error(error.response?.data?.detail || 'Failed to save role');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;

    setIsDeleting(true);
    try {
      await api.delete(`/roles/${roleToDelete.id}`);
      toast.success(`Role "${roleToDelete.name}" deleted successfully!`);
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
      refresh();
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteDialog = (role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const toggleMassActionMode = () => {
    setMassActionMode(!massActionMode);
    if (massActionMode) {
      setSelectedRoles([]);
    }
  };

  // Error state
  if (isError) {
    return (
      <TabPageLayout
        title="Roles & Positions"
        subtitle="Define team positions"
      >
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Failed to load roles</h3>
          <p className="text-sm text-gray-500 mb-4">Please check your connection and try again.</p>
          <PrimaryButton onClick={() => refresh()}>
            Try Again
          </PrimaryButton>
        </div>
      </TabPageLayout>
    );
  }

  return (
    <TabPageLayout
      title="Roles & Positions"
      subtitle="Define team positions"
      loading={isLoading}
      actions={
        <div className="flex items-center gap-2 w-full">
          {/* Mass Actions Toggle */}
          <SecondaryButton
            onClick={toggleMassActionMode}
            className={`h-9 w-9 p-0 flex items-center justify-center ${
              massActionMode ? 'bg-blue-50 border-blue-500 text-blue-600' : ''
            }`}
          >
            <CheckSquare className="w-4 h-4" />
          </SecondaryButton>
          
          {/* Card/List view toggle */}
          <CardListViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
          
          {/* Create button */}
          <PrimaryButton onClick={() => handleOpenDialog()} className="h-9 flex-1 sm:flex-none sm:px-4">
            <Plus className="w-4 h-4 mr-2" />
            Create
          </PrimaryButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Mass Action Toolbar */}
        {massActionMode && (
          <MassActionToolbar
            selectedCount={selectedRoles.length}
            onSelectAll={() => setSelectedRoles(roles.map(r => r.id))}
            onDeselectAll={() => setSelectedRoles([])}
            onDelete={async () => {
              if (window.confirm(`Delete ${selectedRoles.length} role(s)? This cannot be undone.`)) {
                try {
                  await Promise.all(selectedRoles.map(id => api.delete(`/roles/${id}`)));
                  toast.success(`Deleted ${selectedRoles.length} role(s)`);
                  setSelectedRoles([]);
                  refresh();
                } catch (error) {
                  toast.error('Failed to delete some roles');
                }
              }
            }}
            itemName="role"
          />
        )}

        {/* Roles Display */}
        {roles.length === 0 ? (
          <EmptyState
            icon={Shield}
            title="No Roles Created Yet"
            description="Create your first role to start organizing your team"
            actionLabel="Create First Role"
            onAction={() => handleOpenDialog()}
          />
        ) : viewMode === "card" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roles.map((role) => {
              const isSelected = selectedRoles.includes(role.id);
              return (
                <ResourceCard
                  key={role.id}
                  id={role.id}
                  name={role.name}
                  icon={Shield}
                  iconColor={role.color || '#3B82F6'}
                  primaryBadge={{
                    label: 'Active',
                    color: role.color,
                    variant: 'outline',
                    className: 'mt-1'
                  }}
                  description={role.description || 'No description provided'}
                  leftBorderColors={[role.color || '#3B82F6']}
                  massActionMode={massActionMode}
                  isSelected={isSelected}
                  onToggleSelect={(e) => {
                    if (e.target.checked) {
                      setSelectedRoles([...selectedRoles, role.id]);
                    } else {
                      setSelectedRoles(selectedRoles.filter(id => id !== role.id));
                    }
                  }}
                  onEdit={() => handleOpenDialog(role)}
                  onDelete={() => openDeleteDialog(role)}
                />
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {massActionMode && (
                      <th className="w-12 px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedRoles.length === roles.length && roles.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRoles(roles.map(r => r.id));
                            } else {
                              setSelectedRoles([]);
                            }
                          }}
                          className="w-4 h-4 cursor-pointer rounded border-gray-300"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {roles.map((role) => {
                    const isSelected = selectedRoles.includes(role.id);
                    return (
                      <tr 
                        key={role.id} 
                        className={`${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'} transition-colors`}
                      >
                        {massActionMode && (
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedRoles([...selectedRoles, role.id]);
                                } else {
                                  setSelectedRoles(selectedRoles.filter(id => id !== role.id));
                                }
                              }}
                              className="w-4 h-4 cursor-pointer rounded border-gray-300"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: `${role.color}20`, border: `2px solid ${role.color}` }}
                            >
                              <Shield className="w-5 h-5" style={{ color: role.color }} />
                            </div>
                            <span className="font-medium text-gray-900">{role.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600">
                            {role.description || 'No description provided'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <Badge 
                            variant="outline"
                            className="border-green-500 text-green-600"
                          >
                            <Check className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDialog(role)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(role)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Create/Edit Role Modal */}
        <StandardModal
          isOpen={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setModalStep(1);
          }}
          title={editingRole ? 'Edit Role' : 'Create New Role'}
          description={modalStep === 1 ? 'Step 1: Basic role information' : 'Step 2: Select which tabs this role can access'}
          size="sm"
          footer={
            <>
              {modalStep === 2 && (
                <SecondaryButton
                  onClick={() => setModalStep(1)}
                  className="flex-1"
                  disabled={isSaving}
                >
                  Back
                </SecondaryButton>
              )}
              {modalStep === 1 ? (
                <>
                  <SecondaryButton
                    onClick={() => setDialogOpen(false)}
                    className="flex-1"
                  >
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton
                    onClick={() => setModalStep(2)}
                    className="flex-1"
                    disabled={!formData.name.trim()}
                  >
                    Next
                  </PrimaryButton>
                </>
              ) : (
                <SuccessButton
                  onClick={handleSaveRole}
                  className="flex-1"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      {editingRole ? 'Update Role' : 'Create Role'}
                    </>
                  )}
                </SuccessButton>
              )}
            </>
          }
        >
          {/* Tab Selector (Only for Editing) */}
          {editingRole && (
            <div className="flex gap-1 mb-4 border-b border-gray-200">
              <button
                type="button"
                onClick={() => setModalStep(1)}
                className={`flex-1 py-2 px-4 text-sm font-medium transition ${
                  modalStep === 1
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Role
              </button>
              <button
                type="button"
                onClick={() => setModalStep(2)}
                className={`flex-1 py-2 px-4 text-sm font-medium transition ${
                  modalStep === 2
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Permissions
              </button>
            </div>
          )}
          
          {/* Step 1: Basic Info */}
          {modalStep === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Role Name <span className="text-red-500">*</span></Label>
                <Input
                  id="name"
                  placeholder="e.g., Captain, Check-In Staff, Manager"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`mt-1 ${!formData.name.trim() && formData.name !== '' ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                />
                {!formData.name.trim() && formData.name !== '' && (
                  <p className="text-sm text-red-500 mt-1">Role name is required</p>
                )}
              </div>

              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <Input
                  id="description"
                  placeholder="Brief description of this role"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Color</Label>
                <div className="flex gap-2 flex-wrap mt-2 p-1">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-10 h-10 rounded-lg border-2 transition-all ${
                        formData.color === color ? 'ring-2 ring-offset-2 ring-blue-500' : 'border-gray-200'
                      }`}
                      style={{ backgroundColor: color }}
                      aria-label={`Select color ${color}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Tab Permissions */}
          {modalStep === 2 && (
            <div className="space-y-4">
              <div className="max-h-96 overflow-y-auto space-y-3 bg-gray-50 p-4 rounded-lg border border-gray-200">
                {getAllTabIds().map(tab => (
                  <div key={tab.id} className="flex items-center space-x-3 p-2 hover:bg-white rounded transition">
                    <Checkbox
                      id={`tab-${tab.id}`}
                      checked={formData.tabPermissions?.[tab.id] === true}
                      onCheckedChange={(checked) => {
                        setFormData({
                          ...formData,
                          tabPermissions: {
                            ...formData.tabPermissions,
                            [tab.id]: checked
                          }
                        });
                      }}
                    />
                    <label
                      htmlFor={`tab-${tab.id}`}
                      className="flex-1 cursor-pointer"
                    >
                      <p className="text-sm font-medium text-gray-900">{tab.label}</p>
                      {tab.group && (
                        <p className="text-xs text-gray-500">{tab.group} section</p>
                      )}
                    </label>
                  </div>
                ))}
              </div>
              
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <p className="text-xs text-blue-700 text-center">
                  <strong>{Object.keys(formData.tabPermissions || {}).filter(k => formData.tabPermissions[k]).length}</strong> tabs will be visible to this role
                </p>
              </div>
            </div>
          )}
        </StandardModal>

        {/* Delete Confirmation Modal */}
        <StandardModal
          isOpen={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          title="Delete Role?"
          description={`Are you sure you want to delete the role "${roleToDelete?.name}"?`}
          size="sm"
          footer={
            <>
              <SecondaryButton
                onClick={() => setDeleteDialogOpen(false)}
                className="flex-1"
                disabled={isDeleting}
              >
                Cancel
              </SecondaryButton>
              <DangerButton
                onClick={handleDeleteRole}
                className="flex-1"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Role'
                )}
              </DangerButton>
            </>
          }
        >
          <InfoCard variant="danger" showIcon={true}>
            <p className="font-medium mb-1">This action cannot be undone.</p>
            <p>This role cannot be deleted if it's assigned to team members or required by assets.</p>
          </InfoCard>
        </StandardModal>
      </div>
    </TabPageLayout>
  );
};

export default RolesTab;
